import React, { useEffect, useState } from 'react';
import { Link, NavLink, Navigate, Routes, Route, useNavigate } from 'react-router-dom';
import { useApp } from '@/contexts/AppContext';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Trash2, Edit3, Plus, X, Share2, RefreshCw, Check } from 'lucide-react';
import AdminSocial from './AdminSocial';
import AdminSettings from './AdminSettings';
import Lightbox from '@/components/Lightbox';

function AdminShell({ children }) {
  const { user, logout } = useApp();
  if (!user || user.role !== 'admin') return <Navigate to="/login" replace />;
  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-12" data-testid="admin-shell">
      <div className="flex justify-between items-end border-b border-border pb-6 mb-10">
        <div>
          <span className="overline text-accent">Admin Console</span>
          <h1 className="font-display text-4xl mt-2">JJBSTYLISH Operations</h1>
        </div>
        <button onClick={logout} className="overline link-underline" data-testid="admin-logout">Sign Out</button>
      </div>
      <nav className="flex gap-6 md:gap-8 border-b border-border mb-10 overflow-x-auto -mx-6 px-6 md:mx-0 md:px-0">
        {[
          { to: '/admin', label: 'Dashboard', end: true },
          { to: '/admin/products', label: 'Products' },
          { to: '/admin/consignments', label: 'Consignments' },
          { to: '/admin/orders', label: 'Orders' },
          { to: '/admin/social', label: 'Social' },
          { to: '/admin/settings', label: 'Settings' },
        ].map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end} className={({ isActive }) => `overline pb-3 whitespace-nowrap ${isActive ? 'border-b-2 border-foreground' : 'text-muted-foreground'}`} data-testid={`admin-nav-${n.label}`}>{n.label}</NavLink>
        ))}
      </nav>
      {children}
    </div>
  );
}

function Dashboard() {
  const { api } = useApp();
  const [s, setS] = useState({});
  const [preview, setPreview] = useState(null); // { would_hide, total_products, sample }
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null); // { hidden }
  const [hiddenCount, setHiddenCount] = useState(0);
  const [bulkOpen, setBulkOpen] = useState(false);

  const refreshHiddenCount = async () => {
    try {
      const { data } = await api.get('/admin/products');
      const n = (data.items || []).filter((p) => p.in_stock === false).length;
      setHiddenCount(n);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    api.get('/admin/stats').then((r) => setS(r.data)).catch(() => {});
    refreshHiddenCount();
    // eslint-disable-next-line
  }, [api]);

  const cards = [
    { k: 'products', label: 'Products' },
    { k: 'users', label: 'Users' },
    { k: 'orders', label: 'Orders' },
    { k: 'consignments', label: 'Consignments' },
    { k: 'pending_consignments', label: 'Pending Reviews' },
  ];

  const previewFix = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/admin/products/hide-broken?dry_run=true');
      setPreview(data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Preview failed'); }
    finally { setBusy(false); }
  };

  const applyFix = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/admin/products/hide-broken');
      setDone(data);
      setPreview(null);
      const r = await api.get('/admin/stats');
      setS(r.data);
      await refreshHiddenCount();
      toast.success(`Hidden ${data.hidden} broken product(s). Nothing was deleted.`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Apply failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
        {cards.map((c) => (
          <div key={c.k} className="border border-border p-6">
            <div className="overline text-muted-foreground">{c.label}</div>
            <div className="font-display text-4xl mt-3" data-testid={`stat-${c.k}`}>{s[c.k] ?? '—'}</div>
          </div>
        ))}
      </div>

      <div className="border-2 border-accent p-6 md:p-8 bg-accent/5" data-testid="actualize-fixes-card">
        <div className="flex items-start gap-3">
          <div className="w-2 h-2 rounded-full bg-accent mt-2 flex-shrink-0" />
          <div className="flex-1">
            <div className="overline text-accent">One-Click Cleanup</div>
            <h2 className="font-display text-2xl md:text-3xl mt-2">Actualize Fixes on Live Site</h2>
            <p className="mt-3 text-sm font-light text-muted-foreground max-w-2xl">
              After every deploy, click this to hide any product that has a broken image or a $0 price from the public storefront.
              <strong className="text-foreground"> Nothing is deleted.</strong> All your product records (titles, designers, descriptions, prices) stay in the database — hidden items just don&apos;t appear on jjbstylish.com until you edit them and re-publish.
            </p>

            {!preview && !done && (
              <button
                type="button"
                onClick={previewFix}
                disabled={busy}
                data-testid="preview-fix-btn"
                className="mt-6 bg-foreground text-background px-8 py-4 overline disabled:opacity-50"
              >
                {busy ? 'Scanning…' : 'Preview What Will Be Hidden →'}
              </button>
            )}

            {preview && !done && (
              <div className="mt-6 border border-border p-5 bg-background" data-testid="preview-panel">
                <div className="font-display text-xl">
                  {preview.would_hide} of {preview.total_products} products will be hidden
                </div>
                <div className="mt-2 text-sm font-light text-muted-foreground">
                  These items have no images or a price of $0. Hiding them removes them from the storefront but keeps them in your admin panel so you can fix each one.
                </div>
                {preview.sample?.length > 0 && (
                  <div className="mt-4 space-y-1.5 max-h-48 overflow-auto">
                    <div className="overline text-muted-foreground">Sample:</div>
                    {preview.sample.map((p) => (
                      <div key={p.id} className="text-xs font-light flex items-center gap-3 py-1 border-b border-border last:border-0">
                        <span className="text-muted-foreground w-16">{p.category || '—'}</span>
                        <span className="flex-1 truncate">{p.title}</span>
                        <span className="text-destructive">${Number(p.price || 0).toLocaleString()}</span>
                        <span className="text-muted-foreground">{p.image_count} img</span>
                      </div>
                    ))}
                  </div>
                )}
                <div className="mt-5 flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={applyFix}
                    disabled={busy || preview.would_hide === 0}
                    data-testid="apply-fix-btn"
                    className="bg-destructive text-white px-6 py-3 overline disabled:opacity-50"
                  >
                    {busy ? 'Hiding…' : `Yes, Hide ${preview.would_hide} Item(s) Now`}
                  </button>
                  <button
                    type="button"
                    onClick={() => setPreview(null)}
                    className="overline link-underline px-3 py-3"
                    data-testid="cancel-fix-btn"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {done && (
              <div className="mt-6 border border-accent p-5 bg-background" data-testid="fix-done-panel">
                <div className="font-display text-xl">Done. {done.hidden} product(s) hidden.</div>
                <div className="mt-2 text-sm font-light text-muted-foreground">
                  Your storefront is now clean. Every hidden item is still in the database with all its metadata. Head to the <NavLink to="/admin/products" className="link-underline text-accent">Products tab</NavLink> to edit each hidden item — upload a fresh photo (now persistent!), set the right price, tick &quot;Published&quot;, save.
                </div>
                <button type="button" onClick={() => setDone(null)} className="mt-4 overline link-underline" data-testid="dismiss-done">Dismiss</button>
              </div>
            )}
          </div>
        </div>
      </div>

      {hiddenCount > 0 && (
        <div className="border border-border p-6 md:p-8" data-testid="bulk-reupload-card">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex-1">
              <div className="overline text-accent">Fix Everything In One Screen</div>
              <h2 className="font-display text-2xl mt-2">Bulk Reupload — {hiddenCount} hidden item{hiddenCount === 1 ? '' : 's'} waiting</h2>
              <p className="mt-2 text-sm font-light text-muted-foreground max-w-2xl">
                Fix multiple products in a row without opening each edit form. For every hidden item you get one row: upload a photo, confirm the price, tap Publish. All the other metadata (title, designer, description) is preserved.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setBulkOpen(true)}
              data-testid="open-bulk-btn"
              className="bg-foreground text-background px-6 py-3 overline whitespace-nowrap"
            >
              Open Bulk Reupload →
            </button>
          </div>
        </div>
      )}

      {bulkOpen && (
        <BulkReuploadModal
          onClose={async () => {
            setBulkOpen(false);
            await refreshHiddenCount();
            const r = await api.get('/admin/stats').catch(() => null);
            if (r) setS(r.data);
          }}
        />
      )}
    </div>
  );
}

function BulkReuploadModal({ onClose }) {
  const { api } = useApp();
  const [items, setItems] = useState([]);      // hidden products
  const [loading, setLoading] = useState(true);
  const [rowState, setRowState] = useState({}); // { [id]: { price, uploading, publishing, done } }
  const [publishedCount, setPublishedCount] = useState(0);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/products');
      const hidden = (data.items || []).filter((p) => p.in_stock === false);
      setItems(hidden);
      // Seed each row's price with the current DB price (may be 0)
      const seed = {};
      hidden.forEach((p) => { seed[p.id] = { price: p.price || '', images: p.images || [] }; });
      setRowState(seed);
    } catch (e) { toast.error('Could not load hidden items'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const updateRow = (id, patch) => setRowState((s) => ({ ...s, [id]: { ...(s[id] || {}), ...patch } }));

  const uploadFor = async (id, file) => {
    if (!file) return;
    updateRow(id, { uploading: true });
    try {
      const fd = new FormData(); fd.append('file', file);
      const { data: up } = await api.post('/uploads', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (!up?.url) throw new Error('No URL returned');
      updateRow(id, { images: [up.url], uploading: false });
      toast.success('Image uploaded');
    } catch (e) {
      updateRow(id, { uploading: false });
      toast.error('Upload failed: ' + (e?.response?.data?.detail || e.message || 'unknown'));
    }
  };

  const publish = async (product) => {
    const row = rowState[product.id] || {};
    const priceNum = parseFloat(row.price);
    if (!priceNum || priceNum <= 0) { toast.error('Enter a price greater than $0'); return; }
    if (!(row.images || []).length) { toast.error('Upload a photo first'); return; }
    if (!product.title?.trim() || !product.designer?.trim()) {
      toast.error('This product is missing a title or designer. Please open the full editor.');
      return;
    }
    updateRow(product.id, { publishing: true });
    try {
      // PUT with the full existing record, overriding price/images/in_stock
      const payload = {
        title: product.title,
        designer: product.designer,
        category: product.category || 'Handbags',
        subcategory: product.subcategory || null,
        price: priceNum,
        original_price: product.original_price ?? null,
        currency: product.currency || 'USD',
        condition: product.condition || 'Excellent',
        size: product.size || null,
        color: product.color || null,
        material: product.material || null,
        description: product.description || '',
        measurements: product.measurements || null,
        images: row.images,
        authenticated: product.authenticated !== false,
        sku: product.sku || null,
        in_stock: true,
      };
      await api.put(`/admin/products/${product.id}`, payload);
      updateRow(product.id, { publishing: false, done: true });
      setPublishedCount((n) => n + 1);
      toast.success(`Published: ${product.title}`);
    } catch (e) {
      updateRow(product.id, { publishing: false });
      toast.error('Publish failed: ' + (e?.response?.data?.detail || e.message || 'unknown'));
    }
  };

  const remaining = items.filter((p) => !rowState[p.id]?.done).length;

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-start md:items-center justify-center p-3 md:p-6 overflow-y-auto" data-testid="bulk-reupload-modal" onClick={onClose}>
      <div className="bg-background max-w-4xl w-full my-4 md:my-0" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-start border-b border-border p-5 md:p-6 sticky top-0 bg-background z-10">
          <div>
            <div className="overline text-accent">Bulk Reupload</div>
            <h2 className="font-display text-2xl mt-1">Fix Hidden Items</h2>
            <p className="text-xs font-light text-muted-foreground mt-1">
              {loading ? 'Loading…' : `${remaining} of ${items.length} still need attention · ${publishedCount} published this session`}
            </p>
          </div>
          <button type="button" onClick={onClose} className="p-2 hover:bg-muted" data-testid="bulk-close-btn" aria-label="Close">
            <X size={20} />
          </button>
        </div>

        <div className="p-5 md:p-6 space-y-3 max-h-[70vh] overflow-y-auto">
          {loading && <div className="text-sm font-light text-muted-foreground">Loading hidden items…</div>}
          {!loading && items.length === 0 && (
            <div className="text-sm font-light text-muted-foreground py-8 text-center" data-testid="bulk-empty">
              No hidden items. Nothing to fix — your storefront is clean.
            </div>
          )}
          {!loading && items.map((p) => {
            const row = rowState[p.id] || {};
            const isDone = !!row.done;
            const currentImg = (row.images || [])[0];
            return (
              <div key={p.id} data-testid={`bulk-row-${p.id}`} className={`border border-border p-4 flex flex-col md:flex-row gap-4 ${isDone ? 'opacity-50 bg-muted/30' : ''}`}>
                {/* image column */}
                <div className="flex-shrink-0">
                  <div className="w-24 h-28 bg-muted overflow-hidden relative border border-border">
                    {currentImg
                      ? <img src={currentImg} alt="" className="w-full h-full object-cover" />
                      : <div className="w-full h-full flex items-center justify-center text-[10px] text-muted-foreground text-center px-1">No image</div>
                    }
                    {row.uploading && <div className="absolute inset-0 bg-background/70 flex items-center justify-center overline text-xs">Uploading…</div>}
                  </div>
                  {!isDone && (
                    <label className="mt-2 block text-center overline text-xs cursor-pointer border border-border py-1.5 hover:bg-foreground hover:text-background transition" data-testid={`bulk-upload-${p.id}`}>
                      {currentImg ? 'Replace Photo' : 'Upload Photo'}
                      <input type="file" accept="image/*" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; uploadFor(p.id, f); }} className="hidden" />
                    </label>
                  )}
                </div>

                {/* details + inputs */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="overline text-muted-foreground">{p.category || '—'}{p.condition ? ` · ${p.condition}` : ''}</div>
                      <div className="font-display text-lg truncate">{p.title || <span className="text-destructive">(missing title)</span>}</div>
                      <div className="text-sm font-light text-muted-foreground truncate">{p.designer || <span className="text-destructive">(missing designer)</span>}</div>
                    </div>
                    {isDone && <span className="overline text-accent flex-shrink-0" data-testid={`bulk-done-${p.id}`}><Check size={14} className="inline mr-1" />Published</span>}
                  </div>

                  {!isDone && (
                    <div className="mt-3 flex flex-col sm:flex-row gap-2 sm:items-center">
                      <div className="flex items-center gap-2 flex-1">
                        <span className="overline text-muted-foreground">Price</span>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={row.price}
                          onChange={(e) => updateRow(p.id, { price: e.target.value })}
                          placeholder="e.g., 4500"
                          className="max-w-[180px]"
                          data-testid={`bulk-price-${p.id}`}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => publish(p)}
                        disabled={row.publishing || row.uploading || !(row.images || []).length || !parseFloat(row.price) || parseFloat(row.price) <= 0}
                        data-testid={`bulk-publish-${p.id}`}
                        className="bg-foreground text-background px-5 py-2 overline disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
                      >
                        {row.publishing ? 'Publishing…' : 'Publish →'}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="border-t border-border p-5 md:p-6 flex flex-col sm:flex-row justify-between gap-3 sticky bottom-0 bg-background z-10">
          <div className="text-xs font-light text-muted-foreground">
            {publishedCount > 0 ? `${publishedCount} product(s) republished this session. Storefront is now live with them.` : 'Nothing published yet.'}
          </div>
          <button type="button" onClick={onClose} className="overline border border-border px-6 py-2.5 hover:bg-foreground hover:text-background transition" data-testid="bulk-done-btn">
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

function ProductForm({ initial, onSave, onCancel }) {
  const [f, setF] = useState(initial || { title: '', designer: '', category: 'Handbags', price: '', condition: 'Excellent', description: '', images: [], authenticated: true, in_stock: true });
  const upd = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const { api } = useApp();
  const [preview, setPreview] = useState(null); // deprecated — kept null; reserved for future
  const [zoom, setZoom] = useState(null); // { images, index }
  const B = process.env.REACT_APP_BACKEND_URL;

  const submit = () => {
    const priceNum = parseFloat(f.price);
    if (!priceNum || priceNum <= 0) { toast.error('Enter a price greater than $0 before saving'); return; }
    if (!f.title?.trim()) { toast.error('Title is required'); return; }
    if (!f.designer?.trim()) { toast.error('Designer is required'); return; }
    if (!(f.images || []).length) { toast.error('Add at least one image'); return; }
    onSave({ ...f, price: priceNum });
  };

  const handlePlainFile = async (e) => {
    // Direct upload — lightweight Pillow-only resize + WebP compress on server.
    // Used for pre-processed photos or when the user just wants to add
    // a picture straight to the product.
    const file = e.target.files?.[0];
    if (!file) { toast.error('No file selected'); return; }
    e.target.value = '';
    try {
      const fd = new FormData(); fd.append('file', file);
      const { data: up } = await api.post('/uploads', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (!up || !up.url) throw new Error('Server returned no URL');
      const imgUrl = up.url;
      setF((s) => ({ ...s, images: [...(s.images || []), imgUrl] }));
      toast.success('Image added');
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Unknown error';
      toast.error('Upload failed: ' + String(msg).slice(0, 120));
      console.error('handlePlainFile error:', err);
    }
  };
  return (
    <div className="border border-border p-6 mb-8" data-testid="product-form">
      <div className="grid grid-cols-2 gap-4">
        <Input placeholder="Title" value={f.title} onChange={(e) => upd('title', e.target.value)} data-testid="pf-title" />
        <Input placeholder="Designer" value={f.designer} onChange={(e) => upd('designer', e.target.value)} data-testid="pf-designer" />
        <select value={f.category} onChange={(e) => upd('category', e.target.value)} className="border border-border p-2" data-testid="pf-category">
          {['Handbags','Watches','Jewelry','Shoes','Women','Men','Accessories'].map((c) => <option key={c}>{c}</option>)}
        </select>
        <Input type="number" placeholder="Price (required, > 0)" value={f.price} onChange={(e) => upd('price', e.target.value)} min="0" step="0.01" data-testid="pf-price" />
        <select value={f.condition} onChange={(e) => upd('condition', e.target.value)} className="border border-border p-2" data-testid="pf-condition">
          {['Pristine','Excellent','Very Good','Good','Fair'].map((c) => <option key={c}>{c}</option>)}
        </select>
        <Input placeholder="Size" value={f.size||''} onChange={(e) => upd('size', e.target.value)} data-testid="pf-size" />
        <Input placeholder="Color" value={f.color||''} onChange={(e) => upd('color', e.target.value)} />
        <Input placeholder="Material" value={f.material||''} onChange={(e) => upd('material', e.target.value)} />
      </div>
      <Textarea placeholder="Description" value={f.description} onChange={(e) => upd('description', e.target.value)} className="mt-4" rows={3} data-testid="pf-description" />
      <div className="mt-4">
        <Label className="overline">Images</Label>
        <div className="flex flex-wrap gap-3 mt-2">
          {(f.images || []).map((src, i) => (
            <div key={i} className="relative w-20 h-24 bg-muted group">
              <img src={src} alt="" className="w-full h-full object-cover cursor-zoom-in" onClick={() => setZoom({ images: (f.images || []).map((u) => ({ url: u })), index: i })} data-testid={`pf-img-${i}`} />
              <button onClick={() => upd('images', f.images.filter((_, j) => j !== i))} className="absolute -top-2 -right-2 bg-foreground text-background rounded-full p-1"><X size={10} /></button>
            </div>
          ))}
          <label className="w-20 h-24 border border-dashed border-border flex flex-col items-center justify-center cursor-pointer gap-1" data-testid="pf-upload-plain" title="Upload a photo">
            <Plus size={14} />
            <span className="text-[8px] font-medium tracking-wider uppercase text-muted-foreground">Upload</span>
            <input type="file" accept="image/*" onChange={handlePlainFile} className="hidden" />
          </label>
        </div>
      </div>
      <div className="mt-4 flex items-center gap-3">
        <label className="inline-flex items-center gap-2 cursor-pointer select-none" data-testid="pf-instock-label">
          <input type="checkbox" checked={!!f.in_stock} onChange={(e) => upd('in_stock', e.target.checked)} data-testid="pf-instock" className="w-4 h-4" />
          <span className="text-sm font-light">
            {f.in_stock ? 'Published — visible on storefront' : 'Draft — hidden from storefront (check to publish)'}
          </span>
        </label>
      </div>
      <div className="mt-6 flex gap-3">
        <button onClick={submit} className="bg-foreground text-background px-6 py-3 overline" data-testid="pf-save">Save</button>
        <button onClick={onCancel} className="overline link-underline">Cancel</button>
      </div>

      {zoom && <Lightbox images={zoom.images} startIndex={zoom.index} onClose={() => setZoom(null)} />}
    </div>
  );
}

function Products() {
  const { api } = useApp();
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [shareNew, setShareNew] = useState(true);
  const [busy, setBusy] = useState({}); // { [productId+action]: true }
  const [confirmDel, setConfirmDel] = useState(null); // product id awaiting delete confirmation
  const load = () => api.get('/admin/products').then((r) => setList(r.data.items || [])).catch(() => toast.error('Failed to load products'));
  useEffect(() => { load(); }, []);

  const mark = (id, action, v) => setBusy((b) => ({ ...b, [`${id}:${action}`]: v }));
  const isBusy = (id, action) => !!busy[`${id}:${action}`];

  const save = async (data) => {
    try {
      if (editing && editing.id) await api.put(`/admin/products/${editing.id}`, data);
      else await api.post(`/admin/products?share_to_social=${shareNew}`, data);
      toast.success(shareNew && !editing?.id ? 'Saved & shared to social' : 'Saved'); setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const doDelete = async (id) => {
    mark(id, 'del', true);
    try {
      await api.delete(`/admin/products/${id}`);
      toast.success('Product deleted');
      setConfirmDel(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed — session may have expired');
    } finally { mark(id, 'del', false); }
  };

  const reshare = async (id) => {
    if (isBusy(id, 'share')) return;
    mark(id, 'share', true);
    try {
      await api.post(`/admin/products/${id}/share`);
      toast.success('Shared to enabled channels');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Share failed');
    } finally { mark(id, 'share', false); }
  };

  const reprocess = async (id) => {
    if (isBusy(id, 'reproc')) return;
    mark(id, 'reproc', true);
    toast.info('Reprocessing images…');
    try {
      await api.post(`/admin/products/${id}/reprocess`);
      toast.success('Images reprocessed');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reprocess failed');
    } finally { mark(id, 'reproc', false); }
  };

  const IconBtn = ({ onClick, title, testId, disabled, danger, children }) => (
    <button type="button" onClick={(e) => { e.stopPropagation(); if (!disabled) onClick?.(); }} disabled={disabled} title={title} aria-label={title} data-testid={testId}
      className={`inline-flex items-center justify-center min-w-[40px] h-10 px-2 border transition ${danger ? 'border-border text-destructive hover:bg-destructive hover:text-white hover:border-destructive' : 'border-border hover:bg-foreground hover:text-background'} disabled:opacity-40 disabled:cursor-not-allowed`}>
      {children}
    </button>
  );

  return (
    <div data-testid="admin-products">
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-display text-2xl">Products ({list.length})</h2>
        <button onClick={() => setEditing({})} className="bg-foreground text-background px-5 py-3 overline" data-testid="new-product">+ New Product</button>
      </div>
      {editing && (
        <>
          {!editing.id && (
            <label className="flex items-center gap-3 cursor-pointer mb-4">
              <input type="checkbox" checked={shareNew} onChange={(e) => setShareNew(e.target.checked)} data-testid="share-new-toggle" />
              <span className="text-sm font-light">Auto-share to social channels after save</span>
            </label>
          )}
          <ProductForm initial={editing.id ? editing : null} onSave={save} onCancel={() => setEditing(null)} />
        </>
      )}
      <div className="space-y-2">
        {list.map((p) => (
          <div key={p.id} className="flex flex-col sm:flex-row sm:items-center gap-3 border border-border p-3" data-testid={`pp-row-${p.id}`}>
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className="w-12 h-14 bg-muted flex-shrink-0">{p.images?.[0] && <img src={p.images[0]} alt="" className="w-full h-full object-cover" />}</div>
              <div className="flex-1 min-w-0">
                <div className="font-display truncate">{p.title}</div>
                <div className="overline text-muted-foreground truncate">{p.designer} · ${Number(p.price || 0).toLocaleString()}</div>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap justify-end">
              <IconBtn onClick={() => reshare(p.id)} title="Share to social" testId={`share-${p.id}`} disabled={isBusy(p.id, 'share')}>
                <Share2 size={16} strokeWidth={1.3} />
              </IconBtn>
              <IconBtn onClick={() => setEditing(p)} title="Edit product" testId={`edit-${p.id}`}>
                <Edit3 size={16} strokeWidth={1.3} />
              </IconBtn>
              <IconBtn onClick={() => reprocess(p.id)} title="Reprocess images" testId={`reproc-${p.id}`} disabled={isBusy(p.id, 'reproc')}>
                <RefreshCw size={16} strokeWidth={1.3} className={isBusy(p.id, 'reproc') ? 'animate-spin' : ''} />
              </IconBtn>
              <IconBtn onClick={() => setConfirmDel(p.id)} title="Delete product" testId={`del-${p.id}`} disabled={isBusy(p.id, 'del')} danger>
                <Trash2 size={16} strokeWidth={1.3} />
              </IconBtn>
            </div>
          </div>
        ))}
      </div>
      {confirmDel && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="confirm-del-modal" onClick={() => setConfirmDel(null)}>
          <div className="bg-background p-6 md:p-8 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-2xl">Delete Product?</h3>
            <p className="mt-2 text-sm font-light text-muted-foreground">This will remove the product permanently. This cannot be undone.</p>
            <div className="mt-6 flex justify-end gap-3">
              <button type="button" onClick={() => setConfirmDel(null)} className="overline link-underline px-3 py-2" data-testid="del-cancel">Cancel</button>
              <button type="button" onClick={() => doDelete(confirmDel)} disabled={isBusy(confirmDel, 'del')} className="bg-destructive text-white px-6 py-3 overline disabled:opacity-50" data-testid="del-confirm">{isBusy(confirmDel, 'del') ? 'Deleting…' : 'Delete'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Consignments() {
  const { api } = useApp();
  const [list, setList] = useState([]);
  const load = () => api.get('/admin/consignments').then((r) => setList(r.data || []));
  useEffect(() => { load(); }, []);  // eslint-disable-line
  const setStatus = async (id, status) => {
    try { await api.put(`/admin/consignments/${id}/status?status=${status}`); toast.success(`Marked ${status}`); load(); }
    catch (e) { toast.error('Failed'); }
  };
  return (
    <div data-testid="admin-consignments">
      <h2 className="font-display text-2xl mb-6">Consignment Queue ({list.length})</h2>
      <div className="space-y-3">
        {list.map((c) => (
          <div key={c.id} className="border border-border p-5" data-testid={`con-${c.id}`}>
            <div className="flex justify-between items-start">
              <div>
                <div className="font-display text-lg">{c.designer} — {c.category}</div>
                <div className="overline text-muted-foreground mt-1">{c.name} · {c.email} · {c.phone || '—'}</div>
              </div>
              <span className="overline text-accent">{c.status}</span>
            </div>
            <p className="mt-3 text-sm font-light">{c.description}</p>
            {c.estimated_value && <div className="mt-2 overline">Est. ${Number(c.estimated_value).toLocaleString()}</div>}
            <div className="mt-4 flex gap-2">
              {['approved','rejected','listed','paid_out'].map((s) => (
                <button key={s} onClick={() => setStatus(c.id, s)} className="overline border border-border px-3 py-1.5 hover:bg-foreground hover:text-background transition" data-testid={`set-${c.id}-${s}`}>{s}</button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Orders() {
  const { api } = useApp();
  const [list, setList] = useState([]);
  const load = () => api.get('/admin/orders').then((r) => setList(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);  // eslint-disable-line
  const refund = async (o) => {
    const amt = window.prompt(`Refund amount for order #${o.id.slice(0,8).toUpperCase()}? (Leave blank for full: $${o.amount})`, '');
    const reason = window.prompt('Reason for refund?', 'Requested by customer') || 'Requested by customer';
    if (!window.confirm(`Confirm refund of ${amt || 'full amount'} for ${o.payment_method} order?`)) return;
    try {
      const { data } = await api.post(`/admin/orders/${o.id}/refund`, { reason, amount: amt ? parseFloat(amt) : null });
      toast.success(`Refunded $${data.amount.toFixed(2)}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Refund failed'); }
  };
  return (
    <div data-testid="admin-orders">
      <h2 className="font-display text-2xl mb-6">Orders ({list.length})</h2>
      <div className="space-y-2">
        {list.map((o) => (
          <div key={o.id} className="border border-border p-4 flex flex-col md:flex-row md:justify-between gap-3" data-testid={`ord-${o.id}`}>
            <div className="flex-1">
              <div className="overline">#{o.id.slice(0,8).toUpperCase()} · {o.payment_method}</div>
              <div className="font-display text-lg mt-1">${o.amount?.toLocaleString()} · {o.user_email || 'guest'}</div>
              <div className="text-xs font-light text-muted-foreground mt-1">{(o.items || []).map(i => i.title).join(', ')}</div>
              {o.refund_amount && <div className="overline text-destructive mt-2">Refunded ${o.refund_amount} · {o.refund_reason}</div>}
            </div>
            <div className="flex items-center gap-3">
              <span className={`overline ${o.status === 'refunded' ? 'text-destructive' : 'text-accent'}`}>{o.status}</span>
              {o.status !== 'refunded' && o.status !== 'cancelled' && (
                <button onClick={() => refund(o)} className="overline border border-border px-3 py-1.5 hover:bg-destructive hover:text-white hover:border-destructive transition" data-testid={`refund-${o.id}`}>Refund</button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Admin() {
  return (
    <AdminShell>
      <Routes>
        <Route index element={<Dashboard />} />
        <Route path="products" element={<Products />} />
        <Route path="consignments" element={<Consignments />} />
        <Route path="orders" element={<Orders />} />
        <Route path="social" element={<AdminSocial />} />
        <Route path="settings" element={<AdminSettings />} />
      </Routes>
    </AdminShell>
  );
}
